import { type ReactElement, useCallback } from 'react'
import { ButtonAdd, Page } from '@components'
import { useAddEventOutcome } from '@front/model'
import { useToastError } from '@hooks'
import { useForm, isNotEmpty } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import type { UnsavedEventOutcome } from '@terros/common'
import { EventOutcomeForm } from './EventOutcomeForm'
import type { EventOutcomeFormData } from './types'
import { fromEventOutcomeFormData } from './util'

function AddOutcome(): ReactElement {
  const navigate = useNavigate()
  const { add, isAdding, error } = useAddEventOutcome()
  useToastError(error)
  const form = useForm<EventOutcomeFormData<UnsavedEventOutcome>>({
    initialValues: {
      name: '',
      closed: 'Disabled',
      reschedule: false,
      occurred: 'Disabled',
      fault: 'none',
      integrationMapping: [],
    },
    validate: {
      name: isNotEmpty('Name is required'),
    },
    validateInputOnChange: true,
  })
  const handleSubmit = useCallback(
    async (unsaved: EventOutcomeFormData<UnsavedEventOutcome>): Promise<void> => {
      await add(fromEventOutcomeFormData(unsaved))
      navigate({ to: '..' })
      form.resetDirty(unsaved)
      notifications.show({ message: 'Outcome added', color: 'green' })
    },
    [add, navigate, form]
  )

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title='Add Outcome'>
          <ButtonAdd disabled={!form.isDirty()} loading={isAdding} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <EventOutcomeForm form={form} />
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/outcome_/add/')({
  component: AddOutcome,
})
